// Finar rev-B 24 — veri seti editörü "N değişiklik" özeti (saf). Kaynak, Kaydet'in kullandığı
// taslak farkıyla aynı: orijinal ↔ taslak satırları, INDICATOR_PATCH_KEYS. Her değişen anlamlı alan
// insan diliyle tek satır olur ("Doğal gaz tüketimi — etiket: A → B"). Birim bu farka GİRMEZ (birim
// varyantı: müşteri × gösterge düzeyinde, IndicatorModal'dan ayrı eylem). Sıra, ham `orderIndex`
// değil KARDEŞLER ARASINDAKİ KONUM (1'den) üzerinden yazılır — taşıma kardeşleri 0..n-1 yeniden
// numaralar; konumu değişmeyen satır için "sıra" satırı üretilmez. Override alanı temizlenince
// (null) katalog değeri bilinmiyorsa "katalog varsayılanı" yazılır. Kaydetme API'si değişmez.

import {
  KPI_AGGREGATION_LABELS,
  KPI_CLIENT_ACCESS_LABELS,
  type KpiSetIndicator,
} from "@mahir/shared";
import { calcGroupChangeLines, type BreakdownSetNode } from "./kpi-breakdown-set";
import { buildIndicatorTree } from "./kpi-set-editor";
import { KPI_COPY } from "./kpi-ui";

export interface SetChange {
  /** satır kimliği (liste anahtarı + seçim) */
  id: string;
  /** "<gösterge> — <ne değişti>" */
  text: string;
}

type Row = Pick<
  KpiSetIndicator,
  | "id"
  | "parentId"
  | "orderIndex"
  | "code"
  | "label"
  | "labelOverride"
  | "aggregation"
  | "aggregationOverride"
  | "aggregationWeightOverride"
  | "formulas"
  | "activeFormulaId"
  | "clientAccess"
  | "linkedQuestionCode"
  | "showChange"
  | "increaseIsGood"
  | "anomalyThreshold"
  | "footnote"
> &
  BreakdownSetNode;

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === "object" && typeof b === "object" && a !== null && b !== null)
    return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

/** kardeşler arasındaki 1 tabanlı konum (ağaç sırası: orderIndex → etiket) */
function positions(list: readonly Row[]): Map<string, number> {
  const tree = buildIndicatorTree([...list]);
  const out = new Map<string, number>();
  for (const sibs of tree.childrenOf.values()) sibs.forEach((s, i) => out.set(s.id, i + 1));
  return out;
}

/**
 * Orijinal ↔ taslak → bekleyen değişiklik satırları (ağaç sırasıyla). Taslakta olup orijinalde
 * olmayan satır "eklendi", orijinalde olup taslakta olmayan "çıkarıldı" olur (bugün ekleme/çıkarma
 * sunucuya anında gider; özet yine de iki yönü de söyler).
 */
export function describeSetChanges(original: readonly Row[], draft: readonly Row[]): SetChange[] {
  const C = KPI_COPY.setChanges;
  const byId = new Map(original.map((o) => [o.id, o]));
  const draftIds = new Set(draft.map((d) => d.id));
  const labelOf = new Map<string, string>();
  for (const r of [...original, ...draft]) if (!labelOf.has(r.id)) labelOf.set(r.id, r.label);
  const posBefore = positions(original);
  const posAfter = positions(draft);
  const out: SetChange[] = [];
  const add = (id: string, name: string, what: string): void => {
    out.push({ id, text: `${name} — ${what}` });
  };
  // ağaç sırası (taslak) — liste kullanıcının gördüğü sırayla
  const ordered = buildIndicatorTree([...draft]).rows.map((r) => r.node);
  const calcChanges = new Map(calcGroupChangeLines(original, draft).map((l) => [l.id, l.text]));

  for (const d of ordered) {
    const o = byId.get(d.id);
    if (!o) {
      add(d.id, d.label, C.added);
      continue;
    }
    const name = o.label;
    if (!same(o.labelOverride, d.labelOverride)) {
      const next = d.labelOverride ?? (o.labelOverride === null ? o.label : C.catalogDefault);
      add(d.id, name, C.label(o.label, next));
    }
    if (!same(o.parentId, d.parentId)) {
      const parentName = (id: string | null): string =>
        id === null ? C.root : (labelOf.get(id) ?? C.none);
      add(d.id, name, C.parent(parentName(o.parentId), parentName(d.parentId)));
    } else if (!same(o.orderIndex, d.orderIndex)) {
      const a = posBefore.get(d.id);
      const b = posAfter.get(d.id);
      if (a !== undefined && b !== undefined && a !== b) add(d.id, name, C.order(a, b));
    }
    if (!same(o.formulas, d.formulas)) add(d.id, name, C.formula);
    else if (!same(o.activeFormulaId, d.activeFormulaId)) add(d.id, name, C.activeFormula);
    if (!same(o.showChange, d.showChange)) add(d.id, name, d.showChange ? C.changeOn : C.changeOff);
    if (!same(o.clientAccess, d.clientAccess))
      add(
        d.id,
        name,
        C.clientAccess(
          KPI_CLIENT_ACCESS_LABELS[o.clientAccess],
          KPI_CLIENT_ACCESS_LABELS[d.clientAccess],
        ),
      );
    if (!same(o.aggregationOverride, d.aggregationOverride)) {
      const before = KPI_AGGREGATION_LABELS[o.aggregation];
      const after = d.aggregationOverride
        ? KPI_AGGREGATION_LABELS[d.aggregationOverride]
        : o.aggregationOverride === null
          ? before
          : C.catalogDefault;
      add(d.id, name, C.aggregation(before, after));
    }
    if (!same(o.aggregationWeightOverride, d.aggregationWeightOverride)) add(d.id, name, C.weight);
    if (!same(o.linkedQuestionCode, d.linkedQuestionCode))
      add(d.id, name, C.question(o.linkedQuestionCode ?? C.none, d.linkedQuestionCode ?? C.none));
    if (!same(o.increaseIsGood, d.increaseIsGood))
      add(d.id, name, C.increase(C.direction(o.increaseIsGood), C.direction(d.increaseIsGood)));
    if (!same(o.anomalyThreshold, d.anomalyThreshold))
      add(
        d.id,
        name,
        C.anomaly(
          o.anomalyThreshold === null ? C.none : `%${o.anomalyThreshold.toString()}`,
          d.anomalyThreshold === null ? C.none : `%${d.anomalyThreshold.toString()}`,
        ),
      );
    if (!same(o.footnote, d.footnote)) add(d.id, name, C.footnote);
    // Kırılım: hesap grubu değişimi (açık seçim ya da grup sırası — NULL kuralı orderIndex'e bakar)
    const calc = calcChanges.get(d.id);
    if (calc) add(d.id, name, calc);
  }
  for (const o of original) if (!draftIds.has(o.id)) add(o.id, o.label, C.removed);
  return out;
}
